import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { AuthCookieContext } from './context';
import useCookie from './useCookie';
import { getLandingRegister } from './../api/api';
import { defaultsDeep, set } from 'lodash';
import trackAmplitude from '../services/analytics';
import { WEB_NEW_USER } from './AmplitudeTrackEvents';
import { DEFAULT_TEMPLATE_ID, FLOW_LIST } from './constants';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { initTikTokPixel } from '../services/initTikTokPixel';
import { templatesRegistry } from './templates';
import { initFbPixel } from 'services/initFbPixel';
import { initGoogleAnalytics } from 'services/initGoogleAnalytics';
import { initAxonPixel } from 'services/initAxonPixel';

function extractTemplateID(pathname) {
  if (!pathname) return null;
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 0 ? segments[0] : null;
}

const AuthCookieProvider = ({ children }) => {
  const { i18n } = useTranslation();

  const templateIdStorage =
    localStorage.getItem('templateId') ||
    extractTemplateID(window.location.pathname);

  const templateIds = templatesRegistry.getTemplateIds();
  const templateId = templateIds.includes(templateIdStorage)
    ? templateIdStorage
    : DEFAULT_TEMPLATE_ID;

  const template = useMemo(
    () => templatesRegistry.getConfig(templateId),
    [templateId]
  );

  const [userInfo, setUserInfo] = useState({
    templateId,
    paypal: {
      selectedPaymentId: template?.priceVariants?.[0]?.paypal,
      paymentId: template?.priceVariants?.[0]?.paypal,
      bestOfferPaymentId: template?.priceVariants?.[0]?.paypal,
    },
    stripe: {
      selectedPaymentId: template?.priceVariants?.[0]?.stripe,
      paymentId: template?.priceVariants?.[0]?.stripe,
      bestOfferPaymentId: template?.priceVariants?.[0]?.stripe,
    },
    locale: 'en',
  });

  useCookie({ data: userInfo });

  const loadUserInfo = useCallback(async () => {
    if (
      templatesRegistry.getConfig(userInfo?.templateId)?.getTemplateIdForABTest
    )
      return;
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const debug = urlParams.get('debug') || '0';

    // const pathname = window.location.pathname;

    let flow = FLOW_LIST.join('/');

    const json = await getLandingRegister({
      debug,
      flow,
      templateId: userInfo?.templateId,
    });

    const newLocale = json?.locale || 'en';
    const oldLocale = localStorage.getItem('smmhub_locale');

    if (oldLocale !== newLocale) {
      localStorage.setItem('smmhub_locale', newLocale);
      dayjs.locale(newLocale);
      i18n.changeLanguage(newLocale);
    }

    setUserInfo((prev) => defaultsDeep(json, prev));

    if (json.is_first_query) {
      trackAmplitude(WEB_NEW_USER, {
        screen: json.screen_id,
        user_id: json.user_id,
      });
    }

    if (!userInfo?.countryCode) {
      try {
        const r = await fetch('https://ipapi.co/json/');
        const { country_code } = await r.json();
        setUserInfo((prev) => ({
          ...set(prev, 'countryCode', country_code),
        }));
      } catch (error) {
        console.error(error);
      }
    }
  }, [i18n, userInfo?.countryCode, userInfo?.templateId]);

  useEffect(() => {
    loadUserInfo();
  }, [loadUserInfo]);

  useLayoutEffect(() => {
    const templateId = userInfo?.templateId;
    if (templateId) {
      const template = templatesRegistry.getConfig(templateId);
      localStorage.setItem('templateId', templateId);
      const isSpecialOffer = sessionStorage.getItem('specialOfferEnabled');

      if (isSpecialOffer) {
        templatesRegistry.updateConfig(templateId, {
          priceVariants: template.specialOfferPriceVariants,
        });
      }

      const defaultPriceVariantIndex =
        template?.uiConfig?.paywallPage?.defaultPriceVariantIndex;

      const priceVariants =
        template?.priceVariants?.[defaultPriceVariantIndex || 0];

      const paypal = {
        selectedPaymentId: priceVariants?.paypal,
        paymentId: priceVariants?.paypal,
        bestOfferPaymentId: priceVariants?.paypal,
      };
      const stripe = {
        selectedPaymentId: priceVariants?.stripe,
        paymentId: priceVariants?.stripe,
        bestOfferPaymentId: priceVariants?.stripe,
      };
      setUserInfo((prev) => ({
        ...prev,
        paypal: { ...prev.paypal, ...paypal },
        stripe: { ...prev.stripe, ...stripe },
      }));

      initTikTokPixel(templateId);
      initFbPixel(templateId);
      initGoogleAnalytics(templateId);
      initAxonPixel(templateId);
    }
  }, [template, userInfo?.templateId]);

  console.log(userInfo);
  return (
    <AuthCookieContext.Provider
      value={{
        userInfo,
        setUserInfo,
        loadUserInfo,
        templateConfig: template,
      }}
    >
      {children}
    </AuthCookieContext.Provider>
  );
};

export default AuthCookieProvider;
